import React from 'react'
import { Document, Page, Text, View, Svg, Path, G } from '@react-pdf/renderer'
import { formatMoney } from 'utils/helpers'
import { arrayToReadableString } from 'utils/strings'
import PropTypes from 'prop-types'
import CarePlan from 'model/carePlans/carePlan'
import styles from './styles'

const {
  heading,
  titleBlock,
  introductionStyles,
  secondaryText,
  tableRow,
  ratingWrapper,
  avatar,
  ratingCount
} = styles

// suggested_doctors and suggested_facilities render sections used to use
// an adjacent component Card.js. However, after the update of
// @react-pdf/renderer 3.1.7 -> 3.4.4, this stopped working and
// only by rendering the document in a single file, could the bug be fixed.
// This shit right here is why we can't have nice things

const Pdf = ({ carePlan = {} }) => {
  const {
    title,
    introduction,
    suggested_doctors = [],
    suggested_facilities = []
  } = carePlan

  return (
    <Document>
      <Page size="A4" style={{ padding: 30 }}>
        <View style={titleBlock}>
          <Text style={heading}>Care Plan: {title}</Text>
          <Svg
            viewBox="0 0 88 80"
            style={{ width: 20, height: 19, marginBottom: 8 }}
          >
            <Path
              d="M48.8733 25.1482V0H31.5256V31.3207H0V48.6684H25.3531C28.4959 37.2722 37.4824 28.2857 48.8787 25.1428L48.8733 25.1482Z"
              fill="#12275E"
            />
            <Path
              d="M60.3932 59.5525L87.6708 40.1456L60.3932 20.4421V31.3262H57.8164C43.3205 31.3262 31.5253 43.1213 31.5253 57.6173V80H48.873V57.6173C48.873 52.6846 52.8838 48.6739 57.8164 48.6739H60.3932V59.5579V59.5525Z"
              fill="#12275E"
            />
          </Svg>
        </View>
        {introduction ? (
          <View>
            <Text style={heading}>Care Plan Introduction</Text>
            <Text style={introductionStyles}>{introduction}</Text>
          </View>
        ) : null}
        {suggested_doctors.length ? (
          <>
            <View>
              <Text style={heading}>Providers</Text>
            </View>
            <View>
              {suggested_doctors.map(entity => {
                const {
                  profile = {},
                  cost = {},
                  navigator_notes = ''
                } = entity || {}
                const {
                  contacts = {},
                  full_name = '',
                  specialty_names = [],
                  rating
                } = profile || {}
                const { copay, deductible, insurance_copay, total, user_pays } =
                  cost || {}
                const details = [
                  { name: 'Copay', value: copay },
                  { name: 'Deductible', value: deductible },
                  { name: 'Co-insurance', value: insurance_copay },
                  { name: 'Total', value: total },
                  { name: 'Member Pays', value: user_pays }
                ]

                return (
                  <View>
                    <View style={{ flexDirection: 'row', paddingBottom: 20 }}>
                      <Svg viewBox="0 0 448 512" width={30} height={30}>
                        <G fill="none" fillRule="evenodd">
                          <Path
                            fill="#183153"
                            d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-96 55.2C54 332.9 0 401.3 0 482.3C0 498.7 13.3 512
                            29.7 512H418.3c16.4 0 29.7-13.3 29.7-29.7c0-81-54-149.4-128-171.1V362c27.6 7.1 48 32.2 48 62v40c0
                            8.8-7.2 16-16 16H336c-8.8 0-16-7.2-16-16s7.2-16 16-16V424c0-17.7-14.3-32-32-32s-32 14.3-32 32v24c8.8 0
                            16 7.2 16 16s-7.2 16-16 16H256c-8.8 0-16-7.2-16-16V424c0-29.8 20.4-54.9
                            48-62V304.9c-6-.6-12.1-.9-18.3-.9H178.3c-6.2 0-12.3 .3-18.3 .9v65.4c23.1 6.9 40 28.3 40 53.7c0 30.9-25.1
                            56-56 56s-56-25.1-56-56c0-25.4 16.9-46.8 40-53.7V311.2zM144 448a24 24 0 1 0 0-48 24 24 0 1 0 0 48z"
                          />
                        </G>
                      </Svg>
                    </View>
                    <View>
                      <Text style={heading}>{full_name}</Text>
                      <Text style={secondaryText}>
                        {!!specialty_names.length &&
                          specialty_names.map(
                            (specialty, n) =>
                              specialty &&
                              `${specialty}${
                                specialty_names[n + 1] ? ',' : ''
                              } `
                          )}
                      </Text>
                      <Text style={secondaryText}>{contacts?.address}</Text>
                      <Text style={secondaryText}>{contacts?.phone}</Text>
                      <View style={ratingWrapper}>
                        {[1, 2, 3, 4, 5].map(i => (
                          <Svg
                            viewBox="0 0 100 100"
                            width={22}
                            height={22}
                            key={`${i}svg`}
                          >
                            <G fill="none" fillRule="evenodd">
                              <Path
                                fill={rating >= i ? '#fadb14' : '#f0f0f0'}
                                d="M63.893,24.277c-0.238-0.711-0.854-1.229-1.595-1.343l-19.674-3.006L33.809,1.15
                              C33.479,0.448,32.773,0,31.998,0s-1.48,0.448-1.811,1.15l-8.815,18.778L1.698,22.935c-0.741,0.113-1.356,0.632-1.595,1.343
                              c-0.238,0.71-0.059,1.494,0.465,2.031l14.294,14.657L11.484,61.67c-0.124,0.756,0.195,1.517,0.822,1.957
                              c0.344,0.243,0.747,0.366,1.151,0.366c0.332,0,0.666-0.084,0.968-0.25l17.572-9.719l17.572,9.719c0.302,0.166,0.636,0.25,0.968,0.25
                              c0.404,0,0.808-0.123,1.151-0.366c0.627-0.44,0.946-1.201,0.822-1.957l-3.378-20.704l14.294-14.657
                              C63.951,25.771,64.131,24.987,63.893,24.277z"
                              />
                            </G>
                          </Svg>
                        ))}
                        <Text style={ratingCount}>{rating}</Text>
                      </View>
                      <Text style={{ ...secondaryText, marginTop: 10 }}>
                        {navigator_notes}
                      </Text>
                    </View>
                    <View>
                      {details.map(({ name, value }) => (
                        <View style={tableRow}>
                          <Text>{name}</Text>
                          <Text>{formatMoney(value)}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )
              })}
            </View>
          </>
        ) : null}
        {suggested_facilities.length ? (
          <>
            <View>
              <Text style={heading}>Facilities</Text>
            </View>
            <View>
              {suggested_facilities.map(entity => {
                const {
                  profile = {},
                  cost = {},
                  navigator_notes = ''
                } = entity || {}
                const {
                  contacts = {},
                  name = '',
                  location_types = []
                } = profile || {}
                const { copay, deductible, insurance_copay, total, user_pays } =
                  cost || {}
                const details = [
                  { name: 'Copay', value: copay },
                  { name: 'Deductible', value: deductible },
                  { name: 'Co-insurance', value: insurance_copay },
                  { name: 'Total', value: total },
                  { name: 'Member Pays', value: user_pays }
                ]

                return (
                  <View>
                    <View style={{ flexDirection: 'row', paddingBottom: 20 }}>
                      <View style={avatar}>
                        <Svg viewBox="0 0 640 512" width={30} height={30}>
                          <G fill="none" fillRule="evenodd">
                            <Path
                              fill="#183153"
                              d="M192 48c0-26.5 21.5-48 48-48H400c26.5 0 48 21.5 48 48V512H368V432c0-26.5-21.5-48-48-48s-48 21.5-48
                                48v80H192V48zM48 96H160V512H48c-26.5 0-48-21.5-48-48V320H80c8.8 0 16-7.2
                                16-16s-7.2-16-16-16H0V224H80c8.8 0 16-7.2 16-16s-7.2-16-16-16H0V144c0-26.5 21.5-48 48-48zm544 0c26.5
                                0 48 21.5 48 48v48H560c-8.8 0-16 7.2-16 16s7.2 16 16 16h80v64H560c-8.8 0-16 7.2-16 16s7.2 16 16
                                16h80V464c0 26.5-21.5 48-48 48H480V96H592zM312 64c-8.8 0-16 7.2-16 16v24H272c-8.8 0-16 7.2-16 16v16c0
                                8.8 7.2 16 16 16h24v24c0 8.8 7.2 16 16 16h16c8.8 0 16-7.2 16-16V152h24c8.8 0 16-7.2
                                16-16V120c0-8.8-7.2-16-16-16H344V80c0-8.8-7.2-16-16-16H312z"
                            />
                          </G>
                        </Svg>
                      </View>
                      <View>
                        <Text style={heading}>{name}</Text>
                        <Text style={secondaryText}>
                          {!!location_types.length &&
                            arrayToReadableString(location_types)}
                        </Text>
                        <Text style={secondaryText}>{contacts?.address}</Text>
                        {contacts?.phones?.map(phone => (
                          <Text key={phone} style={secondaryText}>
                            {phone}
                          </Text>
                        ))}
                        <Text style={{ ...secondaryText, marginTop: 10 }}>
                          {navigator_notes}
                        </Text>
                      </View>
                    </View>
                    <View>
                      {details.map(({ name, value }) => (
                        <View style={tableRow}>
                          <Text>{name}</Text>
                          <Text>{formatMoney(value)}</Text>
                        </View>
                      ))}
                    </View>
                  </View>
                )
              })}
            </View>
          </>
        ) : null}
      </Page>
    </Document>
  )
}

Pdf.propTypes = {
  carePlan: PropTypes.shape(CarePlan.shape).isRequired
}

export default Pdf
